import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaFilePdf, FaArrowLeft, FaImage, FaDownload, FaQrcode, FaCopy } from 'react-icons/fa';
import { sampleAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #545b62;
  }
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
  flex: 1;
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
    font-size: 1.5rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;

  &.edit {
    background-color: #007bff;
    color: white;
    
    &:hover {
      background-color: #0056b3;
    }
  }

  &.delete {
    background-color: #dc3545;
    color: white;
    
    &:hover {
      background-color: #c82333;
    }
  }

  &.pdf {
    background-color: #28a745;
    color: white;
    
    &:hover {
      background-color: #1e7e34;
    }
  }

  &.qr-label {
    background-color: #fd7e14;
    color: white;
    
    &:hover {
      background-color: #e8590c;
    }
  }

  &.duplicate {
    background-color: #6f42c1;
    color: white;
    
    &:hover {
      background-color: #5a32a3;
    }
  }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const DetailCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

const DetailSection = styled.div`
  padding: 24px;
  border-bottom: 1px solid #dee2e6;

  &:last-child {
    border-bottom: none;
  }

  h2 {
    color: #495057;
    font-size: 1.25rem;
    margin-bottom: 16px;
    font-weight: 600;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const DetailItem = styled.div`
  label {
    display: block;
    font-weight: 600;
    color: #495057;
    margin-bottom: 4px;
    font-size: 14px;
  }

  .value {
    color: #333;
    font-size: 16px;
    line-height: 1.4;
  }

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
  }
`;

const ImageItem = styled.div`
  position: relative;
  /*CI aspect-ratio: 1;*/
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  img {
    width: 100%;
    height: 100%;
    /*CI object-fit: cover;*/ object-fit: contain;
  }

  &.graphic-software-image img {
    height: auto;
    max-height: 500px;
    object-fit: contain;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DownloadButton = styled.button`
  padding: 8px 12px;
  background-color: #17a2b8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #138496;
  }
`;

const NoImage = styled.div`
  width: 100%;
  height: 150px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 14px;
  flex-direction: column;
  gap: 8px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6c757d;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;

  img {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const SampleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sample, setSample] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchSample = useCallback(async () => {
    try {
      setLoading(true);
      const data = await sampleAPI.getSample(id);
      setSample(data);
      setError(null);
    } catch (err) {
      setError('Error loading sample');
      console.error('Error fetching sample:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSample();
  }, [fetchSample]);

  const [duplicating, setDuplicating] = useState(false);

  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      const clone = await sampleAPI.duplicateSample(id);
      navigate(`/sample/${clone.id}`);
    } catch (err) {
      setError('Error duplicating the sample');
      console.error('Error duplicating sample:', err);
      setDuplicating(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this sample?')) {
      try {
        await sampleAPI.deleteSample(id);
        navigate('/');
      } catch (err) {
        setError('Error deleting the sample');
        console.error('Error deleting sample:', err);
      }
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const pdfBlob = await sampleAPI.generatePDF(id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sample_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error generating PDF');
      console.error('Error generating PDF:', err);
    }
  };

  const handleGenerateQRLabel = async () => {
    try {
      const pdfBlob = await sampleAPI.generateQRLabel(id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `label_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error generating QR label');
      console.error('Error generating QR label:', err);
    }
  };

  const handleDownloadOriginal = (filename) => {
    const downloadUrl = sampleAPI.getOriginalFileUrl(filename);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isTiffFile = (filename) => {
    return filename && (filename.toLowerCase().endsWith('.tiff') || filename.toLowerCase().endsWith('.tif'));
  };

  const renderImage = (filename, alt = 'Image', className = '') => {
    if (!filename) {
      return (
        <NoImage>
          <FaImage size={24} />
          No image
        </NoImage>
      );
    }

    const imageUrl = sampleAPI.getImageUrl(filename);
    const showDownloadButton = isTiffFile(filename);

    return (
      <ImageContainer>
        <ImageItem className={className} onClick={() => setSelectedImage(imageUrl)}>
          <img src={imageUrl} alt={alt} />
        </ImageItem>
        {showDownloadButton && (
          <DownloadButton onClick={() => handleDownloadOriginal(filename)}>
            <FaDownload /> Download original TIFF
          </DownloadButton>
        )}
      </ImageContainer>
    );
  };

  const renderSampleImages = () => {
    if (!sample.sample_images) {
      return (
        <NoImage>
          <FaImage size={24} />
          No sample images
        </NoImage>
      );
    }

    const imageFilenames = sample.sample_images.split(',');
    return (
      <ImageGallery>
        {imageFilenames.map((filename, index) => {
          const imageUrl = sampleAPI.getImageUrl(filename);
          const showDownloadButton = isTiffFile(filename);
          
          return (
            <ImageContainer key={index}>
              <ImageItem onClick={() => setSelectedImage(imageUrl)}>
                <img src={imageUrl} alt={`Sample ${index + 1}`} />
              </ImageItem>
              {showDownloadButton && (
                <DownloadButton onClick={() => handleDownloadOriginal(filename)}>
                  <FaDownload /> Download original TIFF
                </DownloadButton>
              )}
            </ImageContainer>
          );
        })}
      </ImageGallery>
    );
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <div className="spinner"></div>
          Loading sample...
        </LoadingSpinner>
      </Container>
    );
  }

  if (error || !sample) {
    return (
      <Container>
        <ErrorMessage>{error || 'Sample not found'}</ErrorMessage>
        <BackButton onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to list
        </BackButton>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <FaArrowLeft /> Back
        </BackButton>
        <Title>{sample.title_sample}</Title>
        <ActionButtons>
          <Button className="edit" onClick={() => navigate(`/sample/${id}/edit`)}>
            <FaEdit /> Edit
          </Button>
          <Button className="pdf" onClick={handleGeneratePDF}>
            <FaFilePdf /> PDF
          </Button>
          <Button className="qr-label" onClick={handleGenerateQRLabel}>
            <FaQrcode /> QR Label
          </Button>
          <Button className="duplicate" onClick={handleDuplicate} disabled={duplicating}>
            <FaCopy /> {duplicating ? 'Duplicating...' : 'Duplicate'}
          </Button>
          <Button className="delete" onClick={handleDelete}>
            <FaTrash /> Delete
          </Button>
        </ActionButtons>
      </Header>

      <DetailCard>
        <DetailSection>
          <h2>General Information</h2>
          <DetailGrid>
            <DetailItem>
              <label>ID</label>
              <div className="value">{sample.id}</div>
            </DetailItem>
            <DetailItem>
              <label>Title Sample</label>
              <div className="value">{sample.title_sample}</div>
            </DetailItem>
            {sample.description && (
              <DetailItem>
                <label>Description</label>
                <div className="value">{sample.description}</div>
              </DetailItem>
            )}
            <DetailItem>
              <label>Type of Fantasy</label>
              <div className="value">{sample.type_of_fantasy}</div>
            </DetailItem>
            <DetailItem>
              <label>Sample Use</label>
              <div className="value">{sample.sample_use}</div>
            </DetailItem>
            <DetailItem>
              <label>Creation Date</label>
              <div className="value">
                {new Date(sample.date_created).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </DetailItem>
          </DetailGrid>
        </DetailSection>

        <DetailSection>
          <h2>Technical Specifications</h2>
          <DetailGrid>
            <DetailItem>
              <label>Yarn Type</label>
              <div className="value">{sample.yarn_type}</div>
            </DetailItem>
            <DetailItem>
              <label>Yarn Count</label>
              <div className="value">{sample.yarn_count}</div>
            </DetailItem>
            <DetailItem>
              <label>Speed (mt/min)</label>
              <div className="value">{sample.speed_mt_min}</div>
            </DetailItem>
            <DetailItem>
              <label>Resolution (dpi)</label>
              <div className="value">{sample.resolution_dpi}</div>
            </DetailItem>
            <DetailItem>
              <label>Drop Size</label>
              <div className="value">{sample.drop_size}</div>
            </DetailItem>
            {sample.altezza_testina_da_substrato && (
              <DetailItem>
                <label>Printhead Height from Substrate</label>
                <div className="value">{sample.altezza_testina_da_substrato} cm</div>
              </DetailItem>
            )}
            <DetailItem>
              <label>Steaming Time</label>
              <div className="value">{sample.steaming_time}</div>
            </DetailItem>
            {sample.sample_meters && (
              <DetailItem>
                <label>Sample Meters</label>
                <div className="value">{sample.sample_meters}</div>
              </DetailItem>
            )}
            {sample.dyed_sample_weight && (
              <DetailItem>
                <label>Dyed Sample Weight</label>
                <div className="value">{sample.dyed_sample_weight}</div>
              </DetailItem>
            )}
            {sample.dyed_sample_absorption && (
              <DetailItem>
                <label>Dyed Sample Absorption</label>
                <div className="value">{sample.dyed_sample_absorption}</div>
              </DetailItem>
            )}
            {sample.dyed_sample_absorption_percent && (
              <DetailItem>
                <label>Dyed Sample Absorption %</label>
                <div className="value">{sample.dyed_sample_absorption_percent}%</div>
              </DetailItem>
            )}
            <DetailItem>
              <label>Pretreatment</label>
              <div className="value">{sample.pretreatment || 'No'}</div>
            </DetailItem>
            {sample.concentrazione_imbibente && (
              <DetailItem>
                <label>Wetting Agent Concentration</label>
                <div className="value">{sample.concentrazione_imbibente}</div>
              </DetailItem>
            )}
            <DetailItem>
              <label>Washing</label>
              <div className="value">{sample.washing || 'No'}</div>
            </DetailItem>
            {sample.yarn_original_characteristics && (
              <DetailItem>
                <label>Original Characteristics of the Wire</label>
                <div className="value">{sample.yarn_original_characteristics}</div>
              </DetailItem>
            )}
            {sample.oven && (
              <>
                <DetailItem>
                  <label>Predyed in oven</label>
                  <div className="value">Yes</div>
                </DetailItem>
                {sample.oven_time != null && (
                  <DetailItem>
                    <label>Oven Time</label>
                    <div className="value">{sample.oven_time} min</div>
                  </DetailItem>
                )}
                {sample.oven_temperature != null && (
                  <DetailItem>
                    <label>Oven Temperature</label>
                    <div className="value">{sample.oven_temperature} °{sample.oven_temperature_unit || 'C'}</div>
                  </DetailItem>
                )}
              </>
            )}
          </DetailGrid>
        </DetailSection>

        <DetailSection>
          <h2>Graphic Software Files</h2>
          {(sample.graphic_software_files || sample.graphic_software_file || '').split(',').filter(Boolean).map((filename, index) => (
            <div key={filename} style={{ marginBottom: '12px' }}>
              {isTiffFile(filename)
                ? renderImage(filename, `Graphic Software File ${index + 1}`, 'graphic-software-image')
                : <DownloadButton onClick={() => handleDownloadOriginal(filename)}>
                    <FaDownload /> Download {filename}
                  </DownloadButton>}
            </div>
          ))}
        </DetailSection>

        <DetailSection>
          <h2>Sample Images</h2>
          {renderSampleImages()}
        </DetailSection>

        <DetailSection>
          <h2>Knit Sample Result</h2>
          {renderImage(sample.knit_sample_result_image, 'Knit Sample Result')}
        </DetailSection>

        {sample.notes && (
          <DetailSection>
            <h2>Notes</h2>
            <DetailItem className="full-width">
              <div className="value" style={{ whiteSpace: 'pre-wrap' }}>
                {sample.notes}
              </div>
            </DetailItem>
          </DetailSection>
        )}
      </DetailCard>

      {selectedImage && (
        <Modal onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Full size" />
        </Modal>
      )}
    </Container>
  );
};

export default SampleDetail;
